import React, { createContext, useContext, useEffect, useState } from "react";
import { apiGet } from "../utils/api";
import { thisAcademicYear as defaultAcademicYear } from "../utils/utils.statiquedata";

type AcademicYearContextValue = {
  academicYear: string;
  setAcademicYear: (year: string) => void;
  refresh: () => Promise<void>;
};

const AcademicYearContext = createContext<AcademicYearContextValue | null>(null);
const STORAGE_KEY = "ulpgl_academic_year";

export const AcademicYearProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [academicYear, setAcademicYearState] = useState<string>(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved || defaultAcademicYear;
  });

  const setAcademicYear = (year: string) => {
    const value = (year || "").trim();
    if (!value) return;
    setAcademicYearState(value);
    localStorage.setItem(STORAGE_KEY, value);
  };

  const refresh = async () => {
    try {
      // Try to get from admission guide first
      const res = await apiGet("/admission-guide");
      const year = res?.item?.academicYear?.trim();
      if (year) {
        setAcademicYearState(year);
        localStorage.setItem(STORAGE_KEY, year);
        return;
      }
    } catch (e) {
      // ignore
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const value: AcademicYearContextValue = {
    academicYear,
    setAcademicYear,
    refresh,
  };

  return <AcademicYearContext.Provider value={value}>{children}</AcademicYearContext.Provider>;
};

export const useAcademicYear = () => {
  const ctx = useContext(AcademicYearContext);
  if (!ctx) throw new Error("useAcademicYear must be used within AcademicYearProvider");
  return ctx;
};

export { defaultAcademicYear };
